/*
 * Domain area (audit Phase 3.3). Replaces the eight legacy
 * wwwroot/js/Domain/*.js files with one module on the Phase 3.1 core.
 *
 * Two of those files went rather than moved:
 *   Domain/Common.js  -- Domain.LoadTab, no caller since Edit.cshtml stopped
 *                        carrying a second copy of Detail's tabs. The tab
 *                        strip loads itself now (core/tabs).
 *   Domain/Resource.js -- Domain.Resource.NewClick, no caller since the
 *                        "New Resource" footer button was removed; resources
 *                        are created from an Application's Resources tab. The
 *                        body also read two variables before declaring them.
 *
 * Domain admin is a global page: its links must NOT get the current domain id
 * appended (Home.TopMenu.NavigateFromLink's third argument is false), or
 * /Domain becomes /Domain/1 and lands on the PUT route as a 405.
 */
import { ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { all, one } from '../core/dom';
import { serialize } from '../core/forms';
import { url } from '../core/http';
import { confirm, hideModal } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';
import { List as PersonList } from './person';

function value(id: string): string {
    return (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
}

function setDisplay(id: string, shown: boolean): void {
    const node = document.getElementById(id);
    if (node) node.style.display = shown ? '' : 'none';
}

export const Index = {
    /**
     * Row click, delegated through data-action rather than a document-ready
     * bind: the list arrives as a partial swapped into the SPA shell, and a
     * @section scripts block is part of the layout, not the partial.
     */
    RowClick(id: number | string): false {
        const domainId = Number.parseInt(String(id), 10);
        if (!Number.isFinite(domainId) || domainId <= 0) return false;
        window.location.href = url(`Domain/Detail/${domainId}`);
        return false;
    },

    DeleteClick(domainId: number | string, domainName: string): void {
        void confirm({
            title: 'Delete Domain',
            message: `Delete domain "${domainName}"?`,
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Domain/${domainId}`,
                    method: 'DELETE',
                    alertInto: 'DomainListAlertContainer',
                    errorTitle: 'Delete action failed to execute',
                    onSuccess: () => {
                        for (const row of all(`tr.DomainRow[data-id='${domainId}']`)) row.remove();
                        toast('success', `Domain "${domainName}" has been deleted`);
                    },
                });
            },
        });
    },

    DisplayCreateModal(): void {
        void ajaxModal({
            modal: 'DomainModal',
            url: 'Domain/Create',
            formId: 'CreateDomainForm',
            focusId: 'Name',
            errorTitle: 'New Domain',
            errorMessage: 'New Domain View failed to execute:',
        });
    },
};

export const Create = {
    /**
     * POST the create form. The form has no action and no method of its own,
     * so the pre-2026-09 `$(form).submit()` ran the browser's native submit --
     * a GET back to /Domain carrying the antiforgery token in the query
     * string, which re-rendered the list and discarded the entry. Nothing was
     * ever POSTed to Domain/Create.
     */
    SaveClick(): false {
        const form = one('CreateDomainForm') as HTMLFormElement | null;
        if (!form || !isValid(form)) return false;
        void ajaxJson({
            url: 'Domain/Create',
            method: 'POST',
            data: serialize(form),
            busy: '#DomainCreateBusy',
            alertInto: 'DomainCreateMessageContainer',
            errorTitle: 'Failed to create domain',
            onSuccess: () => {
                hideModal('DomainModal');
                toast('success', 'Domain created');
                // Re-render the list behind the modal, at the same URL the
                // rail link uses (no domain id appended -- global page).
                void ajaxSwap({ container: 'ViewContainer', url: 'Domain' });
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (!modelState) return false;
                setValidationModelState(modelState, 'DomainCreateContainer');
                return true;
            },
        });
        return false;
    },
};

export const Edit = {
    /**
     * Until 2026-09-09 this was `return true;` -- "let the native submit
     * proceed" -- against a Save button whose form= named an id that did not
     * exist, on a view nothing navigated to. Nothing was ever saved.
     */
    SaveClick(): false {
        const form = one('DomainEditForm') as HTMLFormElement | null;
        if (!form || !isValid(form)) return false;
        void ajaxJson({
            url: 'Domain/Edit',
            method: 'POST',
            data: serialize(form),
            busy: '#DomainEditBusy',
            alertInto: 'DomainEditMessageContainer',
            errorTitle: 'Failed to save the domain',
            onSuccess: () => {
                hideModal('DomainModal');
                toast('success', 'Domain changes saved');
                // Detail renders the name and URL it was loaded with; re-fetch
                // so the page behind the modal shows what was just saved.
                window.location.reload();
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (!modelState) return false;
                setValidationModelState(modelState, 'DomainEditContainer');
                return true;
            },
        });
        return false;
    },
};

export const Detail = {
    /** Detail.cshtml renders the id as #Id (HiddenFor), not #DomainId. */
    DeleteDomain(): false {
        const domainId = value('Id');
        if (!domainId) return false;
        void confirm({
            title: 'Delete Domain',
            message: 'Delete this domain?',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Domain/${domainId}`,
                    method: 'DELETE',
                    // A refused delete (the Api answers 409 when audit history
                    // references the domain) has to report on THIS page; the
                    // Edit modal's container does not exist here.
                    alertInto: 'DomainDetailMessageContainer',
                    errorTitle: 'An error occurred attempting to delete the domain',
                    onSuccess: () => {
                        toast('success', 'Domain deleted');
                        window.location.href = url('Domain');
                    },
                });
            },
        });
        return false;
    },

    /**
     * Edit.cshtml is already a complete modal-dialog, so no title is passed:
     * showModal drops the partial in whole rather than nesting a second
     * dialog inside this modal's body.
     */
    ShowEdit(): false {
        const domainId = value('Id');
        if (!domainId) return false;
        void ajaxModal({
            modal: 'DomainModal',
            url: `Domain/Edit/${domainId}`,
            formId: 'DomainEditForm',
            focusId: 'Name',
            errorTitle: 'Edit Domain',
            errorMessage: 'Domain Edit failed to load:',
        });
        return false;
    },
};

export const Applications = {
    /**
     * Application detail in a modal. The URL was '/Application/Detail/{id}',
     * which matches no route -- ApplicationController's detail action is
     * GET Application/{resourceId:int} -- so every row click 404'd.
     */
    Detail(appId: number | string): void {
        const id = Number.parseInt(String(appId), 10);
        if (!Number.isFinite(id) || id <= 0) return;
        setDisplay(`DomainApplicationBusy${id}`, true);
        void ajaxModal({
            modal: 'DomainModal',
            url: `Application/${id}`,
            title: 'Application',
            errorTitle: 'Application Detail',
            errorMessage: 'Failed to load application',
            onComplete: () => setDisplay(`DomainApplicationBusy${id}`, false),
        });
    },
};

export const Person = {
    /**
     * Creating a person is the same everywhere it is launched from, and
     * Person/Create binds the new person to the current domain through the
     * CurrentDomainId hidden field, so this delegates rather than duplicating
     * the modal setup.
     */
    NewPerson(): void {
        PersonList.DisplayCreateModal();
    },

    Show(personId: number | string): void {
        setDisplay(`PersonLoading${personId}`, true);
        void ajaxModal({
            modal: 'DomainModal',
            url: `Person/Detail/${personId}`,
            title: `Person ${personId}`,
            errorTitle: `Person ${personId}`,
            errorMessage: 'Person Detail failed to execute:',
            onComplete: () => setDisplay(`PersonLoading${personId}`, false),
        });
    },
};
